import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../api';
import {
  Scale,
  RefreshCw,
  Plus,
  Lock,
  Trophy,
  FileDown,
  CheckCircle2,
  AlertTriangle,
  CalendarRange,
  Eye,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import CageBalancingTabs from '../components/CageBalancingTabs';
import CageDatePicker from '../components/CageDatePicker';
import { useCageDate } from '../contexts/CageDateContext';
import { enterMovesDown } from '../utils/cbsGridNav';
import Can from '../components/Can';
import PinModal from '../components/CagePinModal';
import JackpotHitsCard from '../components/JackpotHitsCard';
import { useAuth } from '../contexts/AuthContext';

// ----------------------------------------------------------------
// Cage Balance Sheet (CBS)
// ----------------------------------------------------------------
// Day/Swing cage reconciliation: denomination counts, ending-inventory
// components, cage activity, shift close (over/short), jackpot meters, and
// the QuickBooks IIF export. Backed by /admin/cbs/*. Gated by cbs:view;
// money/close actions require a cage PIN. UX gating via <Can>; the backend
// independently enforces every permission.

const fmtCents = (c) =>
  c == null
    ? '$0.00'
    : (Number(c) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const SHIFTS = ['day', 'swing'];
const KINDS = [
  { key: 'cash', label: 'Cash', unit: 'Bundle' },
  { key: 'chip', label: 'Live Chips', unit: 'Rack' },
  { key: 'promo', label: 'Promo Chips', unit: 'Rack' },
];
const ACTIVITY_TYPES = [
  { v: 'cash_add', l: 'Cash Add (+)' },
  { v: 'cash_removed', l: 'Cash Removed (−)' },
  { v: 'chip_add', l: 'Chip Add (+)' },
  { v: 'chips_removed', l: 'Chips Removed (−)' },
  { v: 'fabicash', l: 'FABICash (−)' },
  { v: 'atm_fill', l: 'ATM Fill (−)' },
  { v: 'dealer_tray_variance', l: 'Dealer Tray Variance' },
  { v: 'podium_variance', l: 'Podium Variance' },
  { v: 'floor_promo', l: 'Floor Promo' },
  { v: 'morning_bonus', l: 'Morning Bonus (−)' },
  { v: 'free_play_redeemed', l: 'Free Play Redeemed (−)' },
  { v: 'poker_jackpot_payout', l: 'Poker Jackpot Payout (−)' },
  { v: 'tcp_jackpot_payout', l: 'TCP Jackpot Payout (−)' },
  { v: 'uth_jackpot_payout', l: 'UTH Jackpot Payout (−)' },
  { v: 'bj_jackpot_payout', l: 'BJ Jackpot Payout (−)' },
  { v: 'chip_buy', l: 'Chip Buy' },
  { v: 'player_bank', l: 'Player Bank' },
  { v: 'tournament_misc', l: 'Tournament / Misc' },
];
const GAMES = ['HE', 'BJ', 'TCP', 'UTH'];

const dollarsToCents = (v) => Math.round(Number(v || 0) * 100);
const apiErr = (e, fallback) => toast.error(e?.response?.data?.error || fallback);

// Validated jackpot fee splits — mirrors backend utils/cbsCalc.jackpotSplit
// (keep in sync). Drives the live preview so the banker sees meter/admin/reserve
// before posting; the backend recomputes authoritatively on post. All cents.
//   HE  : admin = drop / 25 × 15 (60%); meter = drop − admin; no reserve; no hours.
//   BJ  : meter = drop × 20%; admin = $16 × table hours; reserve = drop − meter − admin.
//   TCP : meter = drop × 20%; admin = $12 × table hours; reserve = drop − meter − admin.
//   UTH : meter = drop × 35%; admin = $16 × table hours; reserve = drop − meter − admin.
const JACKPOT_RULES = {
  HE: { meterPct: null, adminPerHourCents: null, heAdmin: true },
  BJ: { meterPct: 0.2, adminPerHourCents: 1600, heAdmin: false },
  TCP: { meterPct: 0.2, adminPerHourCents: 1200, heAdmin: false },
  UTH: { meterPct: 0.35, adminPerHourCents: 1600, heAdmin: false },
};
function jackpotSplit(game, dropCents, tableHours = 0) {
  const rule = JACKPOT_RULES[game];
  const drop = Math.max(0, Math.round(Number(dropCents) || 0));
  if (!rule) return { meterCents: 0, adminCents: 0, reserveCents: 0 };
  if (rule.heAdmin) {
    const adminCents = Math.round((drop / 25) * 15);
    return { meterCents: drop - adminCents, adminCents, reserveCents: 0 };
  }
  const hrs = Math.max(0, Number(tableHours) || 0);
  const meterCents = Math.round(drop * rule.meterPct);
  const adminCents = Math.round(hrs * rule.adminPerHourCents);
  return { meterCents, adminCents, reserveCents: drop - meterCents - adminCents };
}
const JACKPOT_RULE_HINT = {
  HE: 'admin = drop ÷ 25 × 15 (60%); meter = remainder; no reserve',
  BJ: 'meter = 20% · admin = $16 × hrs · reserve = drop − meter − admin',
  TCP: 'meter = 20% · admin = $12 × hrs · reserve = drop − meter − admin',
  UTH: 'meter = 35% · admin = $16 × hrs · reserve = drop − meter − admin',
};

export default function CageBalanceSheet() {
  const { can } = useAuth();
  const { businessDate, setBusinessDate } = useCageDate();
  const [shifts, setShifts] = useState([]);
  const [denoms, setDenoms] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null); // getShift(activeId)
  const [newShift, setNewShift] = useState('');
  // Which existing shift a new one runs after. '' = place it last.
  const [newAfter, setNewAfter] = useState('');
  const [loading, setLoading] = useState(false);
  const [pin, setPin] = useState(null); // { title, onSubmit }

  const loadShifts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/cbs/shifts', { params: { date: businessDate } });
      setShifts(res.data.shifts || []);
    } catch (e) {
      apiErr(e, 'Failed to load shifts');
    } finally {
      setLoading(false);
    }
  }, [businessDate]);

  useEffect(() => {
    loadShifts();
    setActiveId(null);
    setDetail(null);
  }, [loadShifts]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/cbs/denominations');
        setDenoms(res.data.denominations || []);
      } catch {
        /* cbs:view required */
      }
    })();
  }, []);

  const loadDetail = useCallback(async (id) => {
    if (!id) return;
    try {
      const res = await api.get(`/admin/cbs/shifts/${id}`);
      setDetail(res.data.shift);
    } catch (e) {
      apiErr(e, 'Failed to load shift');
    }
  }, []);

  useEffect(() => {
    if (activeId) loadDetail(activeId);
  }, [activeId, loadDetail]);

  const byShift = useMemo(() => {
    const m = {};
    for (const s of shifts) m[s.shift] = s;
    return m;
  }, [shifts]);

  // afterShift places the new sheet in the day. The operator picks the shift it
  // follows, never a number — the backend turns that into a position. Without
  // one an extra shift goes on the end, which is the graveyard case.
  //
  // Placing a shift AHEAD of a sheet that is already closed will move that
  // sheet's opening balance and over/short, so the server refuses with 409 and
  // explains what it would touch. We surface that text and let the operator
  // confirm rather than deciding for them.
  const openShift = async (shift, afterShift = null, confirmCascade = false) => {
    try {
      const res = await api.post('/admin/cbs/shifts', {
        businessDate, shift, afterShift: afterShift || undefined, confirmCascade: confirmCascade || undefined,
      });
      toast.success(`${shift} shift opened`);
      await loadShifts();
      setActiveId(res.data.shift.id);
      return true;
    } catch (e) {
      const msg = e?.response?.data?.error || e?.response?.data?.message || '';
      if (e?.response?.status === 409 && /confirmCascade/i.test(msg)) {
        // eslint-disable-next-line no-alert
        if (window.confirm(`${msg.replace(/ Re-send with confirmCascade to go ahead\.$/, '')}\n\nOpen it anyway?`)) {
          return openShift(shift, afterShift, true);
        }
        return false;
      }
      apiErr(e, 'Failed to open shift');
      return false;
    }
  };

  const active = detail && detail.id === activeId ? detail : byShift[activeShiftName(shifts, activeId)];

  return (
    <div>
      <CageBalancingTabs />

      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Scale size={22} className="text-amber-400" /> Cage Balance Sheet
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Daily cage reconciliation, jackpot meters, and QuickBooks export.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CageDatePicker />
          <button
            onClick={loadShifts}
            className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center gap-2"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Shift cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        {[...SHIFTS, ...shifts.map((s) => s.shift).filter((n) => !SHIFTS.includes(n))].map((sName) => {
          const s = byShift[sName];
          const isActive = s && s.id === activeId;
          return (
            <div
              key={sName}
              className={`rounded-xl p-4 border ${
                isActive ? 'border-amber-500/60 bg-amber-500/5' : 'border-gray-700 bg-gray-800'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="font-semibold text-white capitalize">{sName} shift</div>
                {s ? (
                  <StatusPill status={s.status} overShort={s.overShortCents} />
                ) : (
                  <span className="text-xs text-gray-500">not opened</span>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2">
                {s ? (
                  <button
                    onClick={() => setActiveId(s.id)}
                    className="px-3 py-1.5 text-sm bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
                  >
                    {isActive ? 'Editing' : 'Open sheet'}
                  </button>
                ) : (
                  <Can permission="cbs:operate">
                    <button
                      onClick={() => openShift(sName)}
                      className="px-3 py-1.5 text-sm bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg flex items-center gap-1"
                    >
                      <Plus size={15} /> Open {sName}
                    </button>
                  </Can>
                )}
                {s && s.status === 'closed' && (
                  <span className="text-xs text-gray-400">
                    Over/Short:{' '}
                    <span
                      className={
                        Number(s.overShortCents) === 0
                          ? 'text-emerald-400'
                          : 'text-red-400 font-medium'
                      }
                    >
                      {fmtCents(s.overShortCents)}
                    </span>
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Can permission="cbs:operate">
        <div className="mb-6 rounded-xl border border-dashed border-gray-600 bg-gray-800/40 p-4">
          <div className="font-semibold text-white">Open an additional shift</div>
          <p className="mt-1 text-xs text-gray-500">
            For an extra shift beyond Day/Swing — a graveyard, a second swing, or one that runs
            between the two. Pick which shift it follows: its beginning inventory carries from
            that shift&apos;s ending, and the next shift along will carry from this one.
          </p>
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-gray-400">Shift name</span>
              <input
                value={newShift}
                onChange={(e) => setNewShift(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && newShift.trim()) { openShift(newShift.trim(), newAfter || null); setNewShift(''); } }}
                placeholder="e.g. grave, mid, swing 2"
                className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-gray-400">Runs after</span>
              <select
                value={newAfter}
                onChange={(e) => setNewAfter(e.target.value)}
                className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
              >
                <option value="">Everything (last of the day)</option>
                {shifts.map((s) => (
                  <option key={s.id} value={s.shift}>{s.shift}{s.status === 'closed' ? ' (closed)' : ''}</option>
                ))}
              </select>
            </label>
            <button
              onClick={() => { const n = newShift.trim(); if (n) { openShift(n, newAfter || null); setNewShift(''); } }}
              disabled={!newShift.trim()}
              className="px-3 py-2 text-sm bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg flex items-center gap-1 disabled:opacity-40"
            >
              <Plus size={15} /> Open shift
            </button>
          </div>
          {newAfter && shifts.some((s) => s.shift === newAfter) && shifts
            .filter((s) => s.status === 'closed')
            .some((s) => shifts.findIndex((x) => x.shift === s.shift) > shifts.findIndex((x) => x.shift === newAfter)) && (
            <p className="mt-3 text-xs text-amber-400">
              A shift placed here comes before a sheet that is already closed. Closing it will
              change that sheet&apos;s opening balance and over/short — you will be asked to
              confirm.
            </p>
          )}
        </div>
      </Can>

      {active && active.status !== 'closed' && (
        <ShiftEditor
          key={active.id}
          shift={active}
          denoms={denoms}
          canOperate={can('cbs:operate')}
          onChanged={() => loadDetail(active.id)}
          onClose={(pinReq) => setPin(pinReq)}
          onClosed={async () => {
            await loadShifts();
            await loadDetail(active.id);
          }}
        />
      )}

      {active && active.status === 'closed' && (
        <div className="rounded-xl border border-gray-700 bg-gray-800 p-5 mb-6">
          <div className="flex items-center gap-2 text-white font-semibold mb-3">
            <CheckCircle2 size={18} className="text-emerald-400" /> {active.shift} shift closed
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Beginning" value={fmtCents(active.beginningInventoryCents)} />
            <Stat label="Ending" value={fmtCents(active.endingInventoryCents)} />
            <Stat label="Activity" value={fmtCents(active.activityTotalCents)} />
            <Stat
              label="Over / Short"
              value={fmtCents(active.overShortCents)}
              tone={Number(active.overShortCents) === 0 ? 'good' : 'bad'}
            />
          </div>

          {/* Every entry on the settled sheet, read-only. Confirming a figure
              used to mean reopening the shift, and a reopen is what lets the
              opening balance drift — 2026-10-07 alone saw five reopen/close
              cycles. Nothing here can write. */}
          <ClosedShiftDetail shift={active} />

          {can('cbs:close_shift') && (
            <button
              onClick={() =>
                setPin({
                  title: `Reopen ${active.shift} shift`,
                  subtitle: 'Enter a supervisor cage PIN to reopen this closed shift.',
                  submitLabel: 'Reopen shift',
                  onSubmit: async (creds) => {
                    await api.post(`/admin/cbs/shifts/${active.id}/reopen`, creds);
                    toast.success('Shift reopened');
                    await loadShifts();
                    await loadDetail(active.id);
                  },
                })
              }
              className="mt-4 flex items-center gap-2 rounded-lg border border-gray-600 px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-700"
            >
              <RefreshCw size={15} /> Reopen shift
            </button>
          )}
        </div>
      )}

      {/* One rhythm for the lower cards. They used to sit in a bare div relying
          on their own margins, which none of them had, so the grid and the two
          full-width cards below it ran straight into each other. gap-6 matches
          the grid's own gutter, so vertical and horizontal spacing agree. */}
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <JackpotsCard businessDate={businessDate} canPost={can('cbs:jackpot')} onRequestPin={setPin} />
          <JeExportCard businessDate={businessDate} />
        </div>

        {/* Jackpot hits and the posted totals. Full width: the posted figure is
            published to players daily and the hit form mirrors a paper form, so
            neither reads well in a half column. */}
        <JackpotHitsCard businessDate={businessDate} onRequestPin={setPin} />

        <JeRangeExportCard businessDate={businessDate} />
      </div>

      {pin && <PinModal req={pin} onDone={() => setPin(null)} onError={apiErr} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ClosedShiftDetail — the settled sheet, read-only
// ---------------------------------------------------------------------------
// Chris Ngo, 2026-10: "Can you make the data entries visible without reopening
// the sheet? Sometimes data gets translated onto the wrong physical balance
// sheet as we move from shift to shift."
//
// Confirming a figure on a closed shift used to require reopening it, and a
// reopen is precisely what lets an opening balance drift: a re-carry on an
// un-closed sheet can silently rewrite it. 2026-10-07 saw five reopen/close
// cycles across three shifts, several seconds apart.
//
// This renders static text, not a disabled form. A closed sheet is a record, so
// it is shown as one — there is no input to mis-click and no request to send.
function ClosedShiftDetail({ shift }) {
  const [open, setOpen] = useState(false);
  const [drift, setDrift] = useState(null);

  // The opening is the figure every other number is measured against, so say
  // whether it still matches the shift before it. A settled sheet is never
  // brought in line automatically, so this is the only place it shows.
  useEffect(() => {
    if (!open || !shift?.id) return;
    let alive = true;
    api.get(`/admin/cbs/shifts/${shift.id}/drift`)
      .then((r) => { if (alive) setDrift(r.data); })
      .catch(() => { if (alive) setDrift(null); });
    return () => { alive = false; };
  }, [open, shift?.id]);

  const counts = shift.inventoryCounts || [];
  const rowsFor = (phase, kind) =>
    counts.filter((c) => c.phase === phase && c.denomKind === kind)
      .sort((a, b) => Number(b.denomCents) - Number(a.denomCents));
  const totalFor = (phase, kind) =>
    rowsFor(phase, kind).reduce((a, r) => a + Number(r.valueCents || 0), 0);

  const components = [
    ['Dealer trays', shift.dealerTrayCents, `cal ${shift.dealerTrayCalCount ?? 0} / poker ${shift.dealerTrayPokerCount ?? 0}`],
    ['Box count', shift.boxCountCents, 'counted drop — offsets ending'],
    ['Floor bank', shift.floorBankCents, ''],
    ['Chip vault', shift.chipVaultCents, ''],
  ];
  const activity = shift.activity || [];
  const beginKinds = KINDS.filter((k) => totalFor('beginning', k.key) || rowsFor('beginning', k.key).length);

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="mt-4 flex items-center gap-2 rounded-lg border border-gray-600 px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-700"
      >
        <Eye size={15} /> View entries
      </button>
    );
  }

  return (
    <div className="mt-4 rounded-lg border border-gray-700 bg-gray-900/40 p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm font-semibold text-white">Entries as recorded</div>
        <button onClick={() => setOpen(false)} className="text-xs text-gray-400 hover:text-gray-200">Hide</button>
      </div>
      <p className="mt-1 text-xs text-gray-500">
        Read-only. Nothing here changes the sheet — reopen the shift only if you need to correct it.
      </p>

      {drift && drift.hasPrior && (
        <div className={`mt-3 rounded-lg border px-3 py-2 text-xs ${
          drift.driftCents === 0
            ? 'border-emerald-800 bg-emerald-900/20 text-emerald-300'
            : 'border-amber-700 bg-amber-900/20 text-amber-300'}`}
        >
          {drift.driftCents === 0
            ? `Opening still matches ${drift.prior}'s ending.`
            : `Opening is ${fmtCents(drift.driftCents)} away from ${drift.prior}'s current ending — that shift changed after this one was closed.`}
        </div>
      )}

      {/* Beginning: stored as one carried total per kind, not per denomination. */}
      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-wide text-gray-400">Beginning inventory</div>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
          {beginKinds.map((k) => {
            const rs = rowsFor('beginning', k.key);
            const carried = rs.length > 0 && rs.every((r) => r.carriedForward);
            return (
              <div key={k.key} className="rounded-lg bg-gray-900/60 border border-gray-700 px-3 py-2">
                <div className="text-xs text-gray-400">{k.label}</div>
                <div className="text-sm text-white font-mono">{fmtCents(totalFor('beginning', k.key))}</div>
                <div className="text-[10px] text-gray-500">{carried ? 'carried forward' : 'entered by hand'}</div>
              </div>
            );
          })}
          {!beginKinds.length && <div className="text-xs text-gray-500">No beginning rows recorded.</div>}
        </div>
      </div>

      {/* Ending: the per-denomination count, which is what people reopen to check. */}
      {KINDS.map((k) => {
        const rs = rowsFor('ending', k.key);
        if (!rs.length) return null;
        return (
          <div key={k.key} className="mt-4">
            <div className="text-[10px] uppercase tracking-wide text-gray-400">{k.label} — ending count</div>
            <table className="mt-1 w-full text-sm">
              <thead>
                <tr className="text-gray-500 text-left">
                  <th className="py-1 pr-3 font-medium">Denom</th>
                  <th className="py-1 px-2 font-medium text-right">{k.unit}s</th>
                  <th className="py-1 px-2 font-medium text-right">Loose</th>
                  <th className="py-1 pl-2 font-medium text-right">Value</th>
                </tr>
              </thead>
              <tbody>
                {rs.map((r) => (
                  <tr key={r.id} className="border-t border-gray-800">
                    <td className="py-1 pr-3 text-gray-300">{fmtCents(r.denomCents)}</td>
                    <td className="py-1 px-2 text-right text-gray-300 font-mono">{r.fullCount}</td>
                    <td className="py-1 px-2 text-right text-gray-300 font-mono">{r.looseCount}</td>
                    <td className="py-1 pl-2 text-right text-white font-mono">{fmtCents(r.valueCents)}</td>
                  </tr>
                ))}
                <tr className="border-t border-gray-700">
                  <td className="py-1 pr-3 text-gray-400" colSpan={3}>{k.label} total</td>
                  <td className="py-1 pl-2 text-right text-white font-mono">{fmtCents(totalFor('ending', k.key))}</td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      })}

      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-wide text-gray-400">Ending components</div>
        <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
          {components.map(([label, cents, hint]) => (
            <div key={label} className="rounded-lg bg-gray-900/60 border border-gray-700 px-3 py-2">
              <div className="text-xs text-gray-400">{label}</div>
              <div className="text-sm text-white font-mono">{fmtCents(cents)}</div>
              {hint && <div className="text-[10px] text-gray-500">{hint}</div>}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-wide text-gray-400">
          Cage activity ({activity.length})
        </div>
        {activity.length === 0 ? (
          <div className="mt-1 text-xs text-gray-500">No activity recorded.</div>
        ) : (
          <table className="mt-1 w-full text-sm">
            <tbody>
              {activity.map((a) => (
                <tr key={a.id} className="border-t border-gray-800">
                  <td className="py-1 pr-3 text-gray-300">{a.activityType}</td>
                  <td className="py-1 px-2 text-gray-500">{a.reasonText || ''}</td>
                  <td className={`py-1 pl-2 text-right font-mono ${Number(a.amountCents) < 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                    {fmtCents(a.amountCents)}
                  </td>
                </tr>
              ))}
              <tr className="border-t border-gray-700">
                <td className="py-1 pr-3 text-gray-400" colSpan={2}>Total</td>
                <td className="py-1 pl-2 text-right text-white font-mono">
                  {fmtCents(activity.reduce((a, x) => a + Number(x.amountCents || 0), 0))}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

    </div>
  );
}

function activeShiftName(shifts, id) {
  const s = shifts.find((x) => x.id === id);
  return s ? s.shift : null;
}

// ---------------------------------------------------------------------------
// Shift editor: inventory counts + components + activity + close
// ---------------------------------------------------------------------------
function ShiftEditor({ shift, denoms, canOperate, onChanged, onClose, onClosed }) {
  const [saving, setSaving] = useState('');
  const [recarrying, setRecarrying] = useState(false);
  // Live mirror of the ComponentsRow form so the summary + over/short reflect
  // edits (e.g. a box count) immediately, before "Save components" is pressed.
  const [liveComp, setLiveComp] = useState(null);

  const recarry = async () => {
    setRecarrying(true);
    try {
      await api.post(`/admin/cbs/shifts/${shift.id}/recarry-beginning`);
      toast.success('Beginning re-synced from the prior shift.');
      await onChanged();
    } catch (e) {
      apiErr(e, 'Failed to re-sync beginning');
    } finally {
      setRecarrying(false);
    }
  };

  const saveCount = async (phase, denomKind, denomCents, fullCount, looseCount) => {
    setSaving(`${phase}:${denomKind}:${denomCents}`);
    try {
      await api.post(`/admin/cbs/shifts/${shift.id}/inventory`, {
        phase,
        denomKind,
        denomCents,
        fullCount: Number(fullCount || 0),
        looseCount: Number(looseCount || 0),
      });
    } catch (e) {
      apiErr(e, 'Failed to save count');
    } finally {
      setSaving('');
    }
  };

  // Live ending-inventory valuation (matches server: denom * (full*100 + loose)).
  // Live ending edits keyed `${kind}:${denomCents}`. Any row not edited this
  // session falls back to the server's saved count, so End Value and the totals
  // render correctly on load (independent of fetch timing) and update live while
  // typing.
  const [endLocal, setEndLocal] = useState({});
  const serverLine = (kind, denomCents) => {
    const c = countFor('ending', kind, denomCents);
    return { full: Number(c.fullCount) || 0, loose: Number(c.looseCount) || 0 };
  };
  const setEnd = (kind, denomCents, patch) =>
    setEndLocal((p) => {
      const k = `${kind}:${denomCents}`;
      const base = p[k] || serverLine(kind, denomCents);
      return { ...p, [k]: { ...base, ...patch } };
    });
  const endLine = (kind, denomCents) =>
    endLocal[`${kind}:${denomCents}`] || serverLine(kind, denomCents);
  const endValueCents = (kind, denomCents) => {
    const c = endLine(kind, denomCents);
    return Number(denomCents) * ((Number(c.full) || 0) * 100 + (Number(c.loose) || 0));
  };
  const kindTotalCents = (kindKey, kindRows) =>
    kindRows.reduce((a, d) => a + endValueCents(kindKey, d.denomCents), 0);
  const sectionEndCents = (kindKey) =>
    denoms.filter((d) => d.kind === kindKey).reduce((a, d) => a + endValueCents(kindKey, d.denomCents), 0);
  // Chip-storage components (valued server-side on the shift): dealer trays, box
  // count, floor bank, chip vault. These count toward total chips.
  const componentsCents = () => {
    if (liveComp) {
      const trays =
        Number(liveComp.dealerTrayCalCount || 0) * dollarsToCents(liveComp.calTrayValueCents) +
        Number(liveComp.dealerTrayPokerCount || 0) * dollarsToCents(liveComp.pokerTrayValueCents);
      return (
        trays +
        dollarsToCents(liveComp.boxCountCents) +
        dollarsToCents(liveComp.floorBankCents) +
        dollarsToCents(liveComp.chipVaultCents)
      );
    }
    return (
      Number(shift.dealerTrayCents || 0) + Number(shift.boxCountCents || 0) +
      Number(shift.floorBankCents || 0) + Number(shift.chipVaultCents || 0)
    );
  };
  const cashEndCents = () => sectionEndCents('cash');
  // Total chips = live + promo denom chips + dealer trays + box + floor bank + vault.
  const totalChipsCents = () => sectionEndCents('chip') + sectionEndCents('promo') + componentsCents();
  const endingInventoryCents = () => cashEndCents() + totalChipsCents();
  // Box/drop counted this shift. It's already inside ending inventory (the Box
  // count component) AND is money that came IN from the tables, so it's booked as
  // a cage inflow that offsets — otherwise the whole drop reads as an over. Uses
  // the live box-count field so it cancels exactly against ending as you type.
  const boxDropCents = () =>
    liveComp ? dollarsToCents(liveComp.boxCountCents) : Number(shift.boxCountCents || 0);
  // Sum of the per-section beginning totals shown in the grid (cash + chips + promo).
  const beginningInventoryCents = () => KINDS.reduce((a, k) => a + beginTotalCents(k.key), 0);
  const cageActivityCents = () =>
    (shift.activity || []).reduce((a, x) => a + Number(x.amountCents || 0), 0);
  // Matches backend closeShift: over/short = total inventory - beginning - activity - box drop.
  const overShortCents = () =>
    endingInventoryCents() - beginningInventoryCents() - cageActivityCents() - boxDropCents();

  // Beginning is a per-section TOTAL (carried from prior ending, or overridden).
  const beginRows = (kind) =>
    (shift.inventoryCounts || []).filter((c) => c.phase === 'beginning' && c.denomKind === kind);
  const beginTotalCents = (kind) => beginRows(kind).reduce((a, r) => a + Number(r.valueCents || 0), 0);
  const beginCarried = (kind) => {
    const rs = beginRows(kind);
    return rs.length === 0 || rs.every((r) => r.carriedForward);
  };
  const saveBeginTotal = async (kind, dollars) => {
    try {
      await api.put(`/admin/cbs/shifts/${shift.id}/beginning-total`, {
        denomKind: kind,
        valueCents: Math.round(Number(dollars) * 100),
      });
      toast.success('Beginning balance updated.');
      await onChanged();
    } catch (e) {
      apiErr(e, 'Failed to set beginning balance');
    }
  };

  const countFor = (phase, kind, denomCents) => {
    const c = (shift.inventoryCounts || []).find(
      (x) => x.phase === phase && x.denomKind === kind && Number(x.denomCents) === Number(denomCents)
    );
    return c || { fullCount: 0, looseCount: 0, valueCents: 0 };
  };

  return (
    <div className="rounded-xl border border-gray-700 bg-gray-800 p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="text-white font-semibold capitalize">
          {shift.shift} shift — data entry
        </div>
        <Can permission="cbs:close_shift">
          <button
            onClick={() =>
              onClose({
                title: `Close ${shift.shift} shift`,
                subtitle: 'Enter a supervisor cage PIN to reconcile and close.',
                submitLabel: 'Close shift',
                onSubmit: async (creds) => {
                  // Closing makes this shift's ending final, which is what the
                  // NEXT shift opens against. If that one is already closed the
                  // server refuses and says what it would move; the operator
                  // decides, not us.
                  const send = async (cascadeClosed) =>
                    api.post(`/admin/cbs/shifts/${shift.id}/close`, { ...creds, ...(cascadeClosed ? { cascadeClosed: true } : {}) });
                  let res;
                  try {
                    res = await send(false);
                  } catch (e) {
                    const msg = e?.response?.data?.error || e?.response?.data?.message || '';
                    if (e?.response?.status === 409 && /cascadeClosed/i.test(msg)) {
                      // eslint-disable-next-line no-alert
                      if (!window.confirm(`${msg.replace(/ Re-send with cascadeClosed to correct the settled sheet too\.$/, '')}\n\nClose it and correct that sheet?`)) return;
                      res = await send(true);
                      toast('Closed, and the following shift was corrected', { icon: '↪️' });
                    } else throw e;
                  }
                  const os = res.data?.summary?.overShortCents;
                  if (os === 0) toast.success('Shift closed — balanced (over/short $0.00)');
                  else toast(`Shift closed — over/short ${fmtCents(os)}`, { icon: '⚠️' });
                  await onClosed();
                },
              })
            }
            className="px-3 py-1.5 text-sm bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center gap-1"
          >
            <Lock size={15} /> Close shift
          </button>
        </Can>
      </div>

      {/* Beginning = per-section total carried from the prior shift's ending. */}
      <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-gray-400">
        <span>Beginning balance carries forward from the prior shift's ending as a per-section total — enter endings only, or type an override total per section.</span>
        {canOperate && (
          <button
            onClick={recarry}
            disabled={recarrying}
            className="px-2 py-1 rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40"
          >
            {recarrying ? 'Re-syncing…' : 'Re-sync from prior shift'}
          </button>
        )}
      </div>

      {/* Inventory counts by kind */}
      <div className="space-y-5">
        {KINDS.map((kind) => {
          const rows = denoms.filter((d) => d.kind === kind.key);
          if (!rows.length) return null;
          return (
            <div key={kind.key}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div className="text-xs uppercase tracking-wide text-gray-400">{kind.label}</div>
                {kind.key !== 'promo' && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-gray-400">
                      Beginning{kind.key === 'chip' ? ' (live + promo)' : ''}
                    </span>
                    <span className="font-mono text-gray-300">
                      {fmtCents(
                        kind.key === 'chip'
                          ? beginTotalCents('chip') + beginTotalCents('promo')
                          : beginTotalCents(kind.key)
                      )}
                    </span>
                    <span className="text-gray-600">{beginCarried(kind.key) ? '(carried)' : '(override)'}</span>
                    {canOperate && (
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="override $"
                        defaultValue=""
                        onBlur={(ev) => {
                          const val = ev.target.value;
                          if (val !== '') { saveBeginTotal(kind.key, val); ev.target.value = ''; }
                        }}
                        className="no-spin w-24 px-2 py-1 bg-gray-900 border border-gray-700 rounded text-right text-white font-mono focus:outline-none focus:border-amber-500"
                      />
                    )}
                  </div>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" onKeyDown={enterMovesDown}>
                  <thead>
                    <tr className="text-gray-500 text-left">
                      <th className="py-1 pr-3 font-medium">Denom</th>
                      <th className="py-1 px-2 font-medium text-right">End {kind.unit}s</th>
                      <th className="py-1 px-2 font-medium text-right">End Loose</th>
                      <th className="py-1 pl-2 font-medium text-right">End Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((d) => {
                      const e = countFor('ending', kind.key, d.denomCents);
                      return (
                        <tr key={d.id} className="border-t border-gray-700/50">
                          <td className="py-1.5 pr-3 text-gray-200 font-mono">{d.label}</td>
                          <CountCell
                            defaultValue={e.fullCount}
                            disabled={!canOperate}
                            onInput={(v) => setEnd(kind.key, d.denomCents, { full: Number(v) || 0 })}
                            onCommit={(v) =>
                              saveCount('ending', kind.key, d.denomCents, v, endLine(kind.key, d.denomCents).loose)
                            }
                          />
                          <CountCell
                            defaultValue={e.looseCount}
                            disabled={!canOperate}
                            onInput={(v) => setEnd(kind.key, d.denomCents, { loose: Number(v) || 0 })}
                            onCommit={(v) =>
                              saveCount('ending', kind.key, d.denomCents, endLine(kind.key, d.denomCents).full, v)
                            }
                          />
                          <td className="py-1.5 pl-2 text-right font-mono text-gray-300">
                            {fmtCents(endValueCents(kind.key, d.denomCents))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-600 text-gray-200 font-semibold">
                      <td className="py-1.5 pr-3">{kind.label} ending total</td>
                      <td colSpan={2}></td>
                      <td className="py-1.5 pl-2 text-right font-mono">
                        {fmtCents(kindTotalCents(kind.key, rows))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          );
        })}
      </div>

      <ComponentsRow shift={shift} canOperate={canOperate} onSaved={onChanged} onValuesChange={setLiveComp} />

      <div className="mt-4 border-t border-gray-700 pt-3 space-y-1">
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">Cash</span>
          <span className="font-mono text-gray-200">{fmtCents(cashEndCents())}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">
            Total chips{' '}
            <span className="text-gray-600">(live + promo + trays + box + floor bank + vault)</span>
          </span>
          <span className="font-mono text-gray-200">{fmtCents(totalChipsCents())}</span>
        </div>
        <div className="flex items-center justify-between border-t border-gray-700 pt-1.5 mt-1.5">
          <span className="text-xs uppercase tracking-wide text-gray-400">Total inventory</span>
          <span className="text-lg font-bold text-white font-mono">{fmtCents(endingInventoryCents())}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">Cage activity</span>
          <span className="font-mono text-gray-200">{fmtCents(cageActivityCents())}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">
            Box drop (in) <span className="text-gray-600">(counted drop, offsets ending)</span>
          </span>
          <span className="font-mono text-gray-200">{fmtCents(boxDropCents())}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">Beginning inventory</span>
          <span className="font-mono text-gray-200">{fmtCents(beginningInventoryCents())}</span>
        </div>
        <div className="flex items-center justify-between border-t border-gray-700 pt-2 mt-1.5">
          <span className="text-xs uppercase tracking-wide text-gray-400">Over / short</span>
          <span
            className={`text-xl font-bold font-mono ${
              overShortCents() === 0 ? 'text-emerald-400' : 'text-red-400'
            }`}
          >
            {fmtCents(overShortCents())}
          </span>
        </div>
      </div>
      <ActivitySection shift={shift} canOperate={canOperate} onChanged={onChanged} />
      {saving && <div className="mt-2 text-xs text-gray-500">Saving…</div>}
    </div>
  );
}

// A count cell renders blank rather than 0. An uncounted denomination and a
// denomination counted as zero look identical on paper - the cage writes nothing
// in either case - and a grid pre-filled with zeros hides which rows have
// actually been keyed. Note this has to catch a literal 0 too, not just null:
// countFor() returns { fullCount: 0, looseCount: 0 } when no count row exists,
// and a cleared field commits 0 and comes back as 0 on the next load.
// Number('') is 0, so nothing downstream changes.
const blankIfZero = (v) => (v == null || Number(v) === 0 ? '' : String(v));

function CountCell({ defaultValue, onCommit, onInput, disabled }) {
  const [v, setV] = useState(() => blankIfZero(defaultValue));
  useEffect(() => setV(blankIfZero(defaultValue)), [defaultValue]);
  return (
    <td className="py-1 px-2 text-right">
      <input
        type="number"
        min="0"
        value={v}
        disabled={disabled}
        onChange={(e) => { setV(e.target.value); if (onInput) onInput(e.target.value); }}
        onBlur={() => Number(v || 0) !== Number(defaultValue ?? 0) && onCommit(v || 0)}
        className="no-spin w-20 px-2 py-1 bg-gray-900 border border-gray-700 rounded text-right text-white font-mono focus:outline-none focus:border-amber-500 disabled:opacity-50"
      />
    </td>
  );
}

function ComponentsRow({ shift, canOperate, onSaved, onValuesChange }) {
  const [f, setF] = useState({
    dealerTrayCalCount: shift.dealerTrayCalCount ?? '',
    dealerTrayPokerCount: shift.dealerTrayPokerCount ?? '',
    calTrayValueCents: shift.calTrayValueCents != null ? shift.calTrayValueCents / 100 : '',
    pokerTrayValueCents: shift.pokerTrayValueCents != null ? shift.pokerTrayValueCents / 100 : '',
    boxCountCents: shift.boxCountCents != null ? shift.boxCountCents / 100 : '',
    floorBankCents: shift.floorBankCents != null ? shift.floorBankCents / 100 : '',
    chipVaultCents: shift.chipVaultCents != null ? shift.chipVaultCents / 100 : '',
  });
  const [status, setStatus] = useState(''); // '' | 'dirty' | 'saving' | 'saved' | 'error'
  const [carriedFrom, setCarriedFrom] = useState(null);
  const prefilledFor = useRef(null);

  // The box count belongs to the Drop Count tab, not to this form. This form's
  // state is seeded once on mount, so a drop committed after that (another tab,
  // the same session) would otherwise leave a stale/blank box field here that
  // the next blur writes back over the committed figure. Follow the server.
  useEffect(() => {
    setF((p) => {
      const next = shift.boxCountCents != null ? shift.boxCountCents / 100 : '';
      return String(p.boxCountCents) === String(next) ? p : { ...p, boxCountCents: next };
    });
  }, [shift.boxCountCents]);
  // Push the live form values up so the summary + over/short recompute as the
  // banker types, without waiting for "Save components".
  useEffect(() => {
    if (onValuesChange) onValuesChange(f);
  }, [f]); // eslint-disable-line react-hooks/exhaustive-deps

  // The trays, floor bank and chip vault are the same figures every day, so a
  // brand-new sheet seeds them from the last shift of this name that had them
  // rather than making someone re-key six identical numbers. Everything stays
  // editable afterwards.
  //
  // Only for an OPEN shift whose components are entirely untouched, so this can
  // never overwrite a real count. The carried values are persisted straight
  // away: they already feed the live over/short through onValuesChange, and
  // values that showed on screen but were never saved would silently drop out
  // of the shift close.
  //
  // boxCountCents is passed through untouched, never carried - it is the day's
  // box drop, and setShiftComponents replaces every field it is given.
  const untouched =
    shift.status !== 'closed' &&
    shift.dealerTrayCalCount == null &&
    shift.dealerTrayPokerCount == null &&
    shift.floorBankCents == null &&
    shift.chipVaultCents == null;

  useEffect(() => {
    if (!canOperate || !untouched) return undefined;
    if (prefilledFor.current === shift.id) return undefined;
    prefilledFor.current = shift.id;
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/admin/cbs/component-defaults', {
          params: { shift: shift.shift, before: shift.businessDate },
        });
        if (!alive || !res.data || !res.data.found) return;
        const c = res.data.components || {};
        setF((p) => ({
          ...p,
          dealerTrayCalCount: c.dealerTrayCalCount ?? '',
          dealerTrayPokerCount: c.dealerTrayPokerCount ?? '',
          calTrayValueCents: c.calTrayValueCents != null ? c.calTrayValueCents / 100 : p.calTrayValueCents,
          pokerTrayValueCents: c.pokerTrayValueCents != null ? c.pokerTrayValueCents / 100 : p.pokerTrayValueCents,
          floorBankCents: c.floorBankCents != null ? c.floorBankCents / 100 : '',
          chipVaultCents: c.chipVaultCents != null ? c.chipVaultCents / 100 : '',
        }));
        setCarriedFrom(res.data.fromBusinessDate);
        await api.put(`/admin/cbs/shifts/${shift.id}/components`, {
          dealerTrayCalCount: c.dealerTrayCalCount,
          dealerTrayPokerCount: c.dealerTrayPokerCount,
          calTrayValueCents: c.calTrayValueCents,
          pokerTrayValueCents: c.pokerTrayValueCents,
          floorBankCents: c.floorBankCents,
          chipVaultCents: c.chipVaultCents,
          boxCountCents: shift.boxCountCents != null ? shift.boxCountCents : null,
        });
        if (alive) onSaved();
      } catch {
        // Pre-fill is a convenience - never let it block or noise up data entry.
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shift.id, canOperate, untouched]);
  const save = async () => {
    setStatus('saving');
    try {
      await api.put(`/admin/cbs/shifts/${shift.id}/components`, {
        dealerTrayCalCount: f.dealerTrayCalCount === '' ? null : Number(f.dealerTrayCalCount),
        dealerTrayPokerCount:
          f.dealerTrayPokerCount === '' ? null : Number(f.dealerTrayPokerCount),
        calTrayValueCents: f.calTrayValueCents === '' ? null : dollarsToCents(f.calTrayValueCents),
        pokerTrayValueCents: f.pokerTrayValueCents === '' ? null : dollarsToCents(f.pokerTrayValueCents),
        boxCountCents: f.boxCountCents === '' ? null : dollarsToCents(f.boxCountCents),
        floorBankCents: f.floorBankCents === '' ? null : dollarsToCents(f.floorBankCents),
        chipVaultCents: f.chipVaultCents === '' ? null : dollarsToCents(f.chipVaultCents),
      });
      setStatus('saved');
      onSaved();
    } catch (e) {
      setStatus('error');
      apiErr(e, 'Failed to save components');
    }
  };
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); setStatus('dirty'); };
  // Auto-persist on blur so ending components survive leaving the page —
  // matches the denomination counts, which save on commit. No manual
  // "Save components" click required (the button remains as an explicit save).
  const autoSave = () => { if (canOperate && status === 'dirty') save(); };
  return (
    <div className="mt-5 pt-4 border-t border-gray-700">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-gray-400">
          Ending components (trays / box / vault)
        </span>
        {carriedFrom && (
          <span className="rounded-full border border-gray-600 bg-gray-900 px-2 py-0.5 text-[11px] text-gray-400">
            trays / floor bank / vault carried from {carriedFrom} &mdash; edit if they changed
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        <CompField label="Cal trays (#)" value={f.dealerTrayCalCount} onChange={set('dealerTrayCalCount')} disabled={!canOperate} onBlur={autoSave} />
        <CompField label="Cal tray value ($)" value={f.calTrayValueCents} onChange={set('calTrayValueCents')} disabled={!canOperate} onBlur={autoSave} money />
        <CompField label="Poker trays (#)" value={f.dealerTrayPokerCount} onChange={set('dealerTrayPokerCount')} disabled={!canOperate} onBlur={autoSave} />
        <CompField label="Poker tray value ($)" value={f.pokerTrayValueCents} onChange={set('pokerTrayValueCents')} disabled={!canOperate} onBlur={autoSave} money />
        <CompField label="Box count ($)" value={f.boxCountCents} onChange={set('boxCountCents')} disabled={!canOperate} onBlur={autoSave} money hint="set by Drop Count once committed" />
        <CompField label="Floor bank ($)" value={f.floorBankCents} onChange={set('floorBankCents')} disabled={!canOperate} onBlur={autoSave} money />
        <CompField label="Chip vault ($)" value={f.chipVaultCents} onChange={set('chipVaultCents')} disabled={!canOperate} onBlur={autoSave} money />
      </div>
      {canOperate && (
        <div className="mt-3 flex items-center gap-3">
          <button
            onClick={save}
            className="px-3 py-1.5 text-sm bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
          >
            Save components
          </button>
          <span className="text-xs">
            {status === 'saving' && <span className="text-gray-400">Saving…</span>}
            {status === 'saved' && <span className="text-emerald-400">Saved ✓</span>}
            {status === 'dirty' && <span className="text-amber-400">Unsaved — saves when you leave a field</span>}
            {status === 'error' && <span className="text-red-400">Save failed — retry</span>}
          </span>
        </div>
      )}
    </div>
  );
}

function CompField({ label, value, onChange, onBlur, disabled, money, hint }) {
  return (
    <div>
      <label className="block text-[11px] text-gray-400 mb-1">{label}</label>
      <input
        type="number"
        min="0"
        step={money ? '0.01' : '1'}
        value={value}
        disabled={disabled}
        onChange={onChange}
        onBlur={onBlur}
        className="no-spin w-full px-2 py-1.5 bg-gray-900 border border-gray-700 rounded text-white font-mono text-sm focus:outline-none focus:border-amber-500 disabled:opacity-50"
      />
      {hint && <div className="mt-0.5 text-[10px] text-gray-500">{hint}</div>}
    </div>
  );
}

function ActivitySection({ shift, canOperate, onChanged }) {
  const [type, setType] = useState(ACTIVITY_TYPES[0].v);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const list = shift.activity || [];
  const [customTypes, setCustomTypes] = useState([]);
  useEffect(() => {
    let alive = true;
    api
      .get('/admin/cbs/activity-types')
      .then((res) => {
        if (alive)
          setCustomTypes((res.data.activityTypes || []).map((t) => ({ v: t.activityKey, l: t.label })));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  const allTypes = useMemo(() => [...ACTIVITY_TYPES, ...customTypes], [customTypes]);

  const add = async () => {
    const cents = dollarsToCents(amount);
    if (!cents) return toast.error('Enter a non-zero amount (use − for money out).');
    try {
      await api.post(`/admin/cbs/shifts/${shift.id}/activity`, {
        activityType: type,
        amountCents: cents,
        reasonText: reason || null,
      });
      setAmount('');
      setReason('');
      toast.success('Activity added');
      onChanged();
    } catch (e) {
      apiErr(e, 'Failed to add activity');
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/cbs/shifts/${shift.id}/activity/${id}`);
      toast.success('Activity removed');
      onChanged();
    } catch (e) {
      apiErr(e, 'Failed to remove activity');
    }
  };

  return (
    <div className="mt-5 pt-4 border-t border-gray-700">
      <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Cage activity</div>
      {canOperate && (
        <div className="flex flex-wrap items-end gap-2 mb-3">
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="px-2 py-1.5 bg-gray-900 border border-gray-700 rounded text-white text-sm focus:outline-none focus:border-amber-500"
          >
            {allTypes.map((a) => (
              <option key={a.v} value={a.v}>
                {a.l}
              </option>
            ))}
          </select>
          <input
            type="number"
            step="0.01"
            placeholder="Amount ($, − for out)"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="no-spin w-40 px-2 py-1.5 bg-gray-900 border border-gray-700 rounded text-white font-mono text-sm focus:outline-none focus:border-amber-500"
          />
          <input
            type="text"
            placeholder="Reason / initials"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-40 px-2 py-1.5 bg-gray-900 border border-gray-700 rounded text-white text-sm focus:outline-none focus:border-amber-500"
          />
          <button
            onClick={add}
            className="px-3 py-1.5 text-sm bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg flex items-center gap-1"
          >
            <Plus size={15} /> Add
          </button>
        </div>
      )}
      {list.length === 0 ? (
        <div className="text-sm text-gray-500">No activity recorded.</div>
      ) : (
        <div className="divide-y divide-gray-700/50">
          {list.map((a) => (
            <div key={a.id} className="flex items-center justify-between py-1.5 text-sm">
              <div className="text-gray-300">
                {(allTypes.find((t) => t.v === a.activityType) || {}).l || a.activityType}
                {a.reasonText ? <span className="text-gray-500"> · {a.reasonText}</span> : null}
              </div>
              <div className="flex items-center gap-3">
                <div
                  className={`font-mono ${
                    Number(a.amountCents) < 0 ? 'text-red-400' : 'text-emerald-400'
                  }`}
                >
                  {fmtCents(a.amountCents)}
                </div>
                {canOperate && (
                  <button
                    onClick={() => remove(a.id)}
                    title="Remove activity"
                    className="text-gray-600 hover:text-red-400"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Jackpot meters
// ---------------------------------------------------------------------------
// One row per game for the date, rather than a game picker and a single Post
// button. The old shape let you post HE, change the dropdown, and post HE again
// without the screen ever telling you it was already done - and jackpot_meters
// is append-only, so a second post silently double-counted the running total
// with nothing available to undo it. Here a posted game shows what is on the
// ledger and offers Void instead of Confirm, so the duplicate has nowhere to
// come from, and a genuine mistake has a way out.
//
// Drop comes from the committed Drop Count; the banker supplies table hours
// (prefilled from TableCaptain where available). A game with no jackpot drop is
// still listed, greyed - "TCP had nothing today" should be visible rather than
// inferred from a missing row.
function JackpotsCard({ businessDate, canPost, onRequestPin }) {
  const [meters, setMeters] = useState([]);
  const [dropsByGame, setDropsByGame] = useState({ HE: 0, BJ: 0, TCP: 0, UTH: 0 });
  const [hoursByGame, setHoursByGame] = useState({});
  const [hours, setHours] = useState({});
  const [touched, setTouched] = useState({});
  const [busyGame, setBusyGame] = useState(null);

  const load = useCallback(async () => {
    try {
      const [mRes, dRes] = await Promise.all([
        api.get('/admin/cbs/jackpots', { params: { date: businessDate } }),
        api.get('/admin/cbs/drop-counts/jackpot-drops', { params: { date: businessDate } }),
      ]);
      setMeters(mRes.data.meters || []);
      setDropsByGame(dRes.data.dropsByGame || { HE: 0, BJ: 0, TCP: 0, UTH: 0 });
    } catch {
      /* cbs:view only */
    }
    try {
      const hRes = await api.get('/admin/cbs/jackpot-table-hours', { params: { date: businessDate } });
      setHoursByGame(hRes.data.available ? hRes.data.hoursByGame || {} : {});
    } catch {
      setHoursByGame({});
    }
  }, [businessDate]);

  useEffect(() => {
    load();
  }, [load]);

  // A new date is a different day's numbers: drop the hours the banker typed for
  // the last one rather than carrying them silently onto this one.
  useEffect(() => {
    setHours({});
    setTouched({});
  }, [businessDate]);

  // Prefill from TableCaptain, but never over something already typed.
  useEffect(() => {
    setHours((prev) => {
      const next = { ...prev };
      for (const g of GAMES) {
        if (g === 'HE') continue;
        const h = hoursByGame[g];
        if (h != null && !touched[g] && (next[g] == null || next[g] === '')) next[g] = String(h);
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoursByGame]);

  // A void and the meter it cancels both stay on the ledger. "Live" is what is
  // still standing: neither a void row nor one that has been voided.
  const voidedIds = useMemo(
    () => new Set(meters.map((m) => m.voidsMeterId).filter(Boolean)),
    [meters]
  );
  const liveByGame = useMemo(() => {
    const out = {};
    for (const m of meters) {
      if (m.voidsMeterId || voidedIds.has(m.id)) continue;
      out[m.game] = m; // list arrives newest-first, so the last write wins
    }
    return out;
  }, [meters, voidedIds]);
  const voidedCount = voidedIds.size;

  const confirm = async (game, dropCents, tableHours) => {
    if (busyGame) return;
    setBusyGame(game);
    try {
      await api.post('/admin/cbs/jackpots', { game, businessDate, dropCents, tableHours });
      toast.success(`${game} meter posted`);
      await load();
    } catch (e) {
      const msg =
        (e && e.response && e.response.data && (e.response.data.error || e.response.data.message)) ||
        'Could not post the meter.';
      toast.error(msg);
    } finally {
      setBusyGame(null);
    }
  };

  // Voiding needs a supervisor PIN and a reason, and both are recorded on the
  // reversing row. Posting stays frictionless; undoing a regulated entry does not.
  const requestVoid = (game, meter) => {
    onRequestPin({
      title: `Void the ${game} meter`,
      subtitle:
        `Appends a reversing entry for ${fmtCents(meter.meterCents)} on ${businessDate}. ` +
        `Nothing is deleted — both rows stay on the ledger and net to zero.`,
      submitLabel: 'Void meter',
      needsReason: true,
      reasonLabel: 'Reason (recorded on the ledger)',
      onSubmit: async ({ employeeNumber, staffPin, reason }) => {
        await api.post(`/admin/cbs/jackpots/${meter.id}/void`, {
          employeeNumber,
          staffPin,
          reason,
        });
        toast.success(`${game} meter voided`);
        await load();
      },
    });
  };

  return (
    <div className="rounded-xl border border-gray-700 bg-gray-800 p-5">
      <div className="mb-1 flex items-center justify-between">
        <div className="flex items-center gap-2 font-semibold text-white">
          <Trophy size={18} className="text-amber-400" /> Jackpot meters
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1.5 rounded-lg border border-gray-700 px-2 py-1 text-xs text-gray-400 hover:text-white hover:border-gray-500"
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>
      <p className="mb-4 text-xs text-gray-500">
        Every game for {businessDate}. Drop comes from the committed Drop Count — confirm each one once.
      </p>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-1 pr-2 font-medium">Game</th>
              <th className="px-2 py-1 text-right font-medium">Drop</th>
              <th className="px-2 py-1 text-right font-medium">Table hrs</th>
              <th className="px-2 py-1 text-right font-medium">Meter</th>
              <th className="px-2 py-1 text-right font-medium">Admin</th>
              <th className="px-2 py-1 text-right font-medium">Reserve</th>
              <th className="py-1 pl-2 font-medium text-right">&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {GAMES.map((g) => {
              const posted = liveByGame[g] || null;
              const dropCents = Number(dropsByGame[g] || 0);
              const needsHours = g !== 'HE';
              const hv = hours[g] ?? '';
              const split = jackpotSplit(g, dropCents, Number(hv || 0));
              const noDrop = !dropCents && !posted;
              const hoursMissing = needsHours && !posted && dropCents > 0 && !Number(hv);
              const badReserve = !posted && split.reserveCents < 0;
              const autoHours =
                needsHours && !touched[g] && hoursByGame[g] != null && !posted;

              // A posted row shows the LEDGER, not a fresh recalculation — if the
              // two ever disagree, the ledger is the fact and you want to see it.
              const show = posted
                ? {
                    meter: posted.meterCents,
                    admin: posted.adminFeeCents,
                    reserve: posted.reserveCents,
                    drop: posted.dropCents,
                    hrs: Number(posted.tableHours || 0),
                  }
                : {
                    meter: split.meterCents,
                    admin: split.adminCents,
                    reserve: split.reserveCents,
                    drop: dropCents,
                    hrs: Number(hv || 0),
                  };

              return (
                <tr
                  key={g}
                  className={`border-t border-gray-700/50 align-middle ${
                    noDrop ? 'text-gray-600' : 'text-gray-300'
                  } ${posted ? 'bg-emerald-500/[0.04]' : ''}`}
                >
                  <td className="py-2 pr-2 font-mono font-medium">
                    <span className="flex items-center gap-1.5">
                      {posted && <CheckCircle2 size={14} className="text-emerald-400" />}
                      {g}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-right font-mono">
                    {noDrop ? '—' : fmtCents(show.drop)}
                  </td>
                  <td className="px-2 py-2 text-right">
                    {!needsHours ? (
                      <span className="text-gray-600" title="HE admin is a flat 60% of drop — no hours">
                        n/a
                      </span>
                    ) : posted ? (
                      <span className="font-mono">{show.hrs || '—'}</span>
                    ) : (
                      <input
                        type="number"
                        step="0.25"
                        value={hv}
                        disabled={noDrop || !canPost}
                        onChange={(e) => {
                          const v = e.target.value;
                          setHours((p) => ({ ...p, [g]: v }));
                          setTouched((p) => ({ ...p, [g]: true }));
                        }}
                        className={`no-spin w-20 rounded border bg-gray-900 px-2 py-1 text-right font-mono text-sm text-white
                                    focus:outline-none focus:border-amber-500 disabled:opacity-30
                                    ${hoursMissing ? 'border-amber-500/60' : 'border-gray-700'}`}
                      />
                    )}
                  </td>
                  <td className="px-2 py-2 text-right font-mono text-emerald-300">
                    {noDrop ? '—' : fmtCents(show.meter)}
                  </td>
                  <td className="px-2 py-2 text-right font-mono text-amber-300">
                    {noDrop ? '—' : fmtCents(show.admin)}
                  </td>
                  <td className="px-2 py-2 text-right font-mono text-sky-300">
                    {g === 'HE' || noDrop ? '—' : fmtCents(show.reserve)}
                  </td>
                  <td className="py-2 pl-2 text-right">
                    {posted ? (
                      <Can permission="cbs:jackpot">
                        <button
                          onClick={() => requestVoid(g, posted)}
                          title="Appends a reversing entry — nothing is deleted"
                          className="rounded-lg border border-gray-600 px-2.5 py-1 text-xs text-gray-300 hover:border-red-500/60 hover:text-red-300"
                        >
                          Void
                        </button>
                      </Can>
                    ) : noDrop ? (
                      <span className="text-xs text-gray-600">no drop</span>
                    ) : (
                      <Can permission="cbs:jackpot">
                        <button
                          onClick={() => confirm(g, dropCents, Number(hv || 0))}
                          disabled={busyGame === g || !canPost}
                          className="rounded-lg bg-amber-500 px-3 py-1 text-xs font-medium text-black hover:bg-amber-600 disabled:opacity-50"
                        >
                          {busyGame === g ? 'Posting…' : 'Confirm'}
                        </button>
                      </Can>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Per-game cautions, kept under the table so the rows stay scannable. */}
      <div className="mt-3 space-y-1">
        {GAMES.map((g) => {
          if (liveByGame[g]) return null;
          const dropCents = Number(dropsByGame[g] || 0);
          if (!dropCents) return null;
          const hv = hours[g] ?? '';
          const split = jackpotSplit(g, dropCents, Number(hv || 0));
          const notes = [];
          if (g !== 'HE' && !Number(hv)) {
            notes.push(
              `${g}: no table hours — admin fee is $${(JACKPOT_RULES[g].adminPerHourCents / 100).toFixed(0)}/hr, so it posts at $0.00.`
            );
          }
          if (split.reserveCents < 0) {
            notes.push(`${g}: admin fee exceeds drop − meter — check the table hours.`);
          }
          return notes.map((n) => (
            <div key={n} className="flex items-center gap-1 text-[11px] text-amber-400">
              <AlertTriangle size={12} /> {n}
            </div>
          ));
        })}
        {!Object.values(dropsByGame).some(Boolean) && (
          <div className="flex items-center gap-1 text-[11px] text-gray-500">
            <AlertTriangle size={12} /> No jackpot drop counted for {businessDate} yet — commit the Drop Count first.
          </div>
        )}
      </div>

      {voidedCount > 0 && (
        <div className="mt-4 border-t border-gray-700/50 pt-3">
          <div className="mb-1 text-[11px] uppercase tracking-wide text-gray-500">
            Voided ({voidedCount})
          </div>
          {meters
            .filter((m) => m.voidsMeterId || voidedIds.has(m.id))
            .map((m) => (
              <div key={m.id} className="flex items-center justify-between py-0.5 text-[11px] text-gray-500">
                <span className="font-mono">
                  {m.game} {m.voidsMeterId ? 'reversal' : 'voided'}
                </span>
                <span className="font-mono">{fmtCents(m.meterCents)}</span>
                <span className="max-w-[55%] truncate text-right">{m.note || ''}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// QuickBooks JE export
// ---------------------------------------------------------------------------
function JeExportCard({ businessDate }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const doPreview = async () => {
    setBusy(true);
    setPreview(null);
    try {
      const res = await api.get(`/admin/cbs/je/${businessDate}/preview`);
      setPreview(res.data.journal);
    } catch (e) {
      apiErr(e, 'Failed to build journal preview');
    } finally {
      setBusy(false);
    }
  };

  const doExport = async () => {
    setBusy(true);
    try {
      const res = await api.post(
        `/admin/cbs/je/${businessDate}/export`,
        {},
        { params: { download: 1 }, responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.intu.iif' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `CBS-${businessDate}.iif`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('QuickBooks IIF downloaded');
    } catch (e) {
      apiErr(e, 'Export failed — is the day balanced?');
    } finally {
      setBusy(false);
    }
  };

  // PDF via the browser's print pipeline (no dependency): render a full journal
  // report — every account incl. $0.00 — and let the user Save as PDF.
  const exportPdf = () => {
    if (!preview) return;
    const win = window.open('', '_blank');
    if (!win) return toast.error('Allow pop-ups to export the PDF.');
    const money = (c) =>
      (Number(c || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const rows = (preview.lines || [])
      .map(
        (l) =>
          `<tr class="${l.debitCents || l.creditCents ? '' : 'z'}"><td>${l.account}</td>` +
          `<td class="r">${l.debitCents ? money(l.debitCents) : ''}</td>` +
          `<td class="r">${l.creditCents ? money(l.creditCents) : ''}</td></tr>`
      )
      .join('');
    const residWord = Math.abs(preview.residualCents) < 100 ? 'rounding' : 'variance — review';
    const resid = preview.residualCents
      ? `<div class="note">$${money(Math.abs(preview.residualCents))} ${residWord} booked to Daily Over/Short.</div>`
      : '';
    win.document.write(
      `<!doctype html><html><head><meta charset="utf-8"><title>CBS Journal ${businessDate}</title>` +
        `<style>@page{size:letter portrait;margin:9mm;}` +
        `body{font:13px/1.2 Arial,Helvetica,sans-serif;color:#111;margin:0;-webkit-print-color-adjust:exact;print-color-adjust:exact;}` +
        `h1{font-size:18px;margin:0 0 2px;}.sub{color:#555;font-size:12px;margin-bottom:8px;}` +
        `table{width:100%;border-collapse:collapse;}th,td{padding:2px 8px;border-bottom:1px solid #ddd;}` +
        `th{text-align:left;background:#f3f3f3;border-bottom:1.5px solid #999;font-size:12px;}` +
        `.r{text-align:right;font-variant-numeric:tabular-nums;}tr.z td{color:#999;}` +
        `tfoot td{font-weight:bold;border-top:1.5px solid #999;border-bottom:none;}` +
        `.note{margin-top:6px;color:#555;font-size:11px;}.bal{margin-top:6px;font-weight:bold;}` +
        `table,tr,thead,tfoot{page-break-inside:avoid;}` +
        `</style></head><body onload="window.print()">` +
        `<h1>EDH Casino — Cage Journal Entry</h1>` +
        `<div class="sub">Business date ${businessDate} · Doc CBS-${businessDate} · General Journal</div>` +
        `<table><thead><tr><th>Account</th><th class="r">Debit</th><th class="r">Credit</th></tr></thead>` +
        `<tbody>${rows}</tbody>` +
        `<tfoot><tr><td>Totals</td><td class="r">${money(preview.totalDebitCents)}</td>` +
        `<td class="r">${money(preview.totalCreditCents)}</td></tr></tfoot></table>` +
        `<div class="bal">${preview.balanced ? 'Balanced — debits equal credits.' : 'OUT OF BALANCE'}</div>${resid}` +
        `</body></html>`
    );
    win.document.close();
  };

  return (
    <div className="rounded-xl border border-gray-700 bg-gray-800 p-5">
      <div className="flex items-center gap-2 text-white font-semibold mb-3">
        <FileDown size={18} className="text-amber-400" /> QuickBooks export — {businessDate}
      </div>
      <p className="text-sm text-gray-400 mb-3">
        Builds the day's journal entry from both shifts and exports a QuickBooks Desktop IIF. Any
        residual variance is booked to Daily Over/Short so the day always foots and can export
        (sub-dollar amounts are rounding; larger amounts are flagged to review).
      </p>
      <div className="flex items-center gap-2 mb-3">
        <button
          onClick={doPreview}
          disabled={busy}
          className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg disabled:opacity-50"
        >
          Preview
        </button>
        <Can permission="cbs:export">
          <button
            onClick={doExport}
            disabled={busy || !preview?.balanced}
            title={preview && !preview.balanced ? 'Journal is out of balance' : ''}
            className="px-3 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg disabled:opacity-50 flex items-center gap-1"
          >
            <FileDown size={15} /> Download IIF
          </button>
        </Can>
        <button
          onClick={exportPdf}
          disabled={busy || !preview}
          title={!preview ? 'Run Preview first' : 'Save the journal as a PDF'}
          className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg disabled:opacity-50 flex items-center gap-1"
        >
          <FileDown size={15} /> Export PDF
        </button>
      </div>
      {preview && (
        <div className="rounded-lg border border-gray-700 bg-gray-900/50 p-3">
          <div className="flex items-center gap-2 mb-2">
            {preview.balanced ? (
              <span className="text-emerald-400 flex items-center gap-1 text-sm">
                <CheckCircle2 size={15} /> Balanced
                {preview.residualCents ? (
                  <span className={Math.abs(preview.residualCents) < 100 ? 'text-gray-500' : 'text-amber-400'}>
                    · {fmtCents(Math.abs(preview.residualCents))}{' '}
                    {Math.abs(preview.residualCents) < 100
                      ? 'rounding booked to Daily Over/Short'
                      : 'variance booked to Daily Over/Short — review'}
                  </span>
                ) : null}
              </span>
            ) : (
              <span className="text-red-400 flex items-center gap-1 text-sm">
                <AlertTriangle size={15} /> Out of balance by {fmtCents(preview.varianceCents)}
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Stat label="Total debits" value={fmtCents(preview.totalDebitCents)} />
            <Stat label="Total credits" value={fmtCents(preview.totalCreditCents)} />
          </div>
          <div className="mt-3 max-h-52 overflow-y-auto text-xs">
            {(preview.lines || []).map((l, i) => {
              const zero = !l.debitCents && !l.creditCents;
              return (
                <div
                  key={i}
                  className={`flex items-center justify-between py-0.5 ${zero ? 'text-gray-600' : 'text-gray-400'}`}
                >
                  <span>{l.account}</span>
                  <span className="font-mono">
                    {l.debitCents ? (
                      <span className="text-gray-200">Dr {fmtCents(l.debitCents)}</span>
                    ) : l.creditCents ? (
                      <span className="text-gray-300">Cr {fmtCents(l.creditCents)}</span>
                    ) : (
                      <span>$0.00</span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// QuickBooks JE export — custom date range
// ---------------------------------------------------------------------------
// One General Journal Entry PER DAY, concatenated into a single .iif, so a month
// imports into QuickBooks as 30 dated entries rather than one lump and every
// line still ties back to a gaming day.
//
// Days that cannot post are skipped and named rather than silently dropped: a
// day with no shifts, a day with a shift still open, a journal that will not
// balance, and — the duplicate-posting guard — a day already exported once.
// Only that last one can be overridden, because the others have no correct
// journal to post in the first place.
function JeRangeExportCard({ businessDate }) {
  const firstOfMonth = (businessDate || '').slice(0, 8) + '01';
  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(businessDate);
  const [includeReexported, setIncludeReexported] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  // Any change to the inputs invalidates the preview — never let the buttons
  // act on a result built from different dates than the ones on screen.
  useEffect(() => { setData(null); }, [from, to, includeReexported]);

  const doPreview = async () => {
    if (!from || !to) return toast.error('Pick a start and end date.');
    if (to < from) return toast.error('End date is before the start date.');
    setBusy(true);
    setData(null);
    try {
      const res = await api.get('/admin/cbs/je/range/preview', {
        params: { from, to, includeReexported: includeReexported ? 1 : 0 },
      });
      setData(res.data);
      if (!res.data.includedCount) toast('Nothing in that range can export yet.', { icon: '!' });
    } catch (e) {
      apiErr(e, 'Failed to build the range preview');
    } finally {
      setBusy(false);
    }
  };

  const doExport = async () => {
    setBusy(true);
    try {
      const res = await api.post(
        '/admin/cbs/je/range/export',
        { from, to, includeReexported },
        { params: { download: 1 }, responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.intu.iif' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `CBS-${from}_to_${to}.iif`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const n = res.headers['x-cbs-exported-days'] || data?.includedCount || '';
      toast.success(`IIF downloaded — ${n} day${String(n) === '1' ? '' : 's'}`);
      doPreview(); // the exported days are now "already exported"
    } catch (e) {
      apiErr(e, 'Range export failed');
    } finally {
      setBusy(false);
    }
  };

  // PDF through the browser's print pipeline (no dependency): a page per day,
  // then a totals page for the range. Same report the single-day card prints.
  const exportPdf = () => {
    if (!data || !data.includedCount) return;
    const win = window.open('', '_blank');
    if (!win) return toast.error('Allow pop-ups to export the PDF.');
    const money = (c) =>
      (Number(c || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const rowsFor = (lines) =>
      (lines || [])
        .map(
          (l) =>
            `<tr class="${l.debitCents || l.creditCents ? '' : 'z'}"><td>${l.account}</td>` +
            `<td class="r">${l.debitCents ? money(l.debitCents) : ''}</td>` +
            `<td class="r">${l.creditCents ? money(l.creditCents) : ''}</td></tr>`
        )
        .join('');
    const included = data.days.filter((d) => d.included);
    const pages = included
      .map((d) => {
        const residWord = Math.abs(d.residualCents) < 100 ? 'rounding' : 'variance — review';
        const resid = d.residualCents
          ? `<div class="note">$${money(Math.abs(d.residualCents))} ${residWord} booked to Daily Over/Short.</div>`
          : '';
        return (
          `<section class="pg">` +
          `<h1>EDH Casino — Cage Journal Entry</h1>` +
          `<div class="sub">Business date ${d.businessDate} · Doc CBS-${d.businessDate} · General Journal</div>` +
          `<table><thead><tr><th>Account</th><th class="r">Debit</th><th class="r">Credit</th></tr></thead>` +
          `<tbody>${rowsFor(d.lines)}</tbody>` +
          `<tfoot><tr><td>Totals</td><td class="r">${money(d.totalDebitCents)}</td>` +
          `<td class="r">${money(d.totalCreditCents)}</td></tr></tfoot></table>${resid}` +
          `</section>`
        );
      })
      .join('');
    const skipRows = (data.skipped || [])
      .map((sk) => `<tr><td>${sk.businessDate}</td><td colspan="2">${sk.reason}</td></tr>`)
      .join('');
    const summary =
      `<section class="pg">` +
      `<h1>EDH Casino — Cage Journal Summary</h1>` +
      `<div class="sub">${data.from} through ${data.to} · ${included.length} day${included.length === 1 ? '' : 's'} · General Journal</div>` +
      `<table><thead><tr><th>Account</th><th class="r">Debit</th><th class="r">Credit</th></tr></thead>` +
      `<tbody>${rowsFor(data.byAccount)}</tbody>` +
      `<tfoot><tr><td>Totals</td><td class="r">${money(data.totals.totalDebitCents)}</td>` +
      `<td class="r">${money(data.totals.totalCreditCents)}</td></tr></tfoot></table>` +
      (data.totals.residualCents
        ? `<div class="note">$${money(Math.abs(data.totals.residualCents))} total booked to Daily Over/Short across the range.</div>`
        : '') +
      (skipRows
        ? `<h2>Days not included</h2><table><tbody>${skipRows}</tbody></table>`
        : '') +
      `</section>`;
    win.document.write(
      `<!doctype html><html><head><meta charset="utf-8"><title>CBS Journal ${data.from} to ${data.to}</title>` +
        `<style>@page{size:letter portrait;margin:9mm;}` +
        `body{font:13px/1.2 Arial,Helvetica,sans-serif;color:#111;margin:0;-webkit-print-color-adjust:exact;print-color-adjust:exact;}` +
        `h1{font-size:18px;margin:0 0 2px;}h2{font-size:14px;margin:14px 0 4px;}` +
        `.sub{color:#555;font-size:12px;margin-bottom:8px;}` +
        `table{width:100%;border-collapse:collapse;}th,td{padding:2px 8px;border-bottom:1px solid #ddd;}` +
        `th{text-align:left;background:#f3f3f3;border-bottom:1.5px solid #999;font-size:12px;}` +
        `.r{text-align:right;font-variant-numeric:tabular-nums;}tr.z td{color:#999;}` +
        `tfoot td{font-weight:bold;border-top:1.5px solid #999;border-bottom:none;}` +
        `.note{margin-top:6px;color:#555;font-size:11px;}` +
        `section.pg{page-break-after:always;}section.pg:last-child{page-break-after:auto;}` +
        `table,tr,thead,tfoot{page-break-inside:avoid;}` +
        `</style></head><body onload="window.print()">${pages}${summary}</body></html>`
    );
    win.document.close();
  };

  const STATUS_TONE = {
    ok: 'text-emerald-400',
    already_exported: 'text-sky-400',
    unclosed: 'text-amber-400',
    unbalanced: 'text-red-400',
    no_data: 'text-gray-500',
  };
  const STATUS_LABEL = {
    ok: 'Ready',
    already_exported: 'Already exported',
    unclosed: 'Shift open',
    unbalanced: 'Out of balance',
    no_data: 'No data',
  };
  const reexportable = (data?.days || []).filter((d) => d.status === 'already_exported').length;

  return (
    <div className="mt-6 rounded-xl border border-gray-700 bg-gray-800 p-5">
      <div className="flex items-center gap-2 text-white font-semibold mb-3">
        <CalendarRange size={18} className="text-amber-400" /> QuickBooks export — date range
      </div>
      <p className="text-sm text-gray-400 mb-4">
        Builds one journal entry per day across the range and exports them as a single QuickBooks
        Desktop IIF — each day keeps its own date and document number, so a month imports as
        separate entries. Days that can&apos;t post are listed rather than dropped.
      </p>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-[11px] text-gray-400 mb-1">From</label>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="px-2 py-1.5 bg-gray-900 border border-gray-700 rounded text-white font-mono text-sm focus:outline-none focus:border-amber-500"
          />
        </div>
        <div>
          <label className="block text-[11px] text-gray-400 mb-1">To</label>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="px-2 py-1.5 bg-gray-900 border border-gray-700 rounded text-white font-mono text-sm focus:outline-none focus:border-amber-500"
          />
        </div>
        <button
          onClick={doPreview}
          disabled={busy}
          className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg disabled:opacity-50"
        >
          {busy ? 'Working…' : 'Preview range'}
        </button>
        <Can permission="cbs:export">
          <button
            onClick={doExport}
            disabled={busy || !data || !data.includedCount}
            title={!data ? 'Run Preview range first' : ''}
            className="px-3 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg disabled:opacity-50 flex items-center gap-1"
          >
            <FileDown size={15} /> Download IIF
            {data?.includedCount ? ` (${data.includedCount})` : ''}
          </button>
        </Can>
        <button
          onClick={exportPdf}
          disabled={busy || !data || !data.includedCount}
          title={!data ? 'Run Preview range first' : 'Save the range as a PDF'}
          className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg disabled:opacity-50 flex items-center gap-1"
        >
          <FileDown size={15} /> Export PDF
        </button>
      </div>

      {(reexportable > 0 || includeReexported) && (
        <label className="mb-4 flex items-start gap-2 text-sm text-gray-300 cursor-pointer">
          <input
            type="checkbox"
            checked={includeReexported}
            onChange={(e) => setIncludeReexported(e.target.checked)}
            className="mt-0.5 accent-amber-500"
          />
          <span>
            Include days already exported to QuickBooks
            {reexportable > 0 && !includeReexported ? ` (${reexportable} in this range)` : ''}
            <span className="block text-xs text-gray-500">
              Off by default — re-importing a day posts the entry a second time in QuickBooks.
            </span>
          </span>
        </label>
      )}

      {data && (
        <div className="rounded-lg border border-gray-700 bg-gray-900/50 p-3">
          <div className="flex flex-wrap items-center gap-4 mb-3 text-sm">
            <span className="text-gray-300">
              <span className="text-white font-semibold">{data.includedCount}</span> of{' '}
              {data.days.length} day{data.days.length === 1 ? '' : 's'} will export
            </span>
            <span className="text-gray-400">
              Debits <span className="font-mono text-gray-200">{fmtCents(data.totals.totalDebitCents)}</span>
            </span>
            <span className="text-gray-400">
              Credits <span className="font-mono text-gray-200">{fmtCents(data.totals.totalCreditCents)}</span>
            </span>
            {data.totals.residualCents ? (
              <span className={Math.abs(data.totals.residualCents) < 100 * data.includedCount ? 'text-gray-500' : 'text-amber-400'}>
                {fmtCents(Math.abs(data.totals.residualCents))} booked to Daily Over/Short
              </span>
            ) : null}
          </div>

          <div className="max-h-64 overflow-y-auto text-xs">
            <table className="w-full">
              <thead className="sticky top-0 bg-gray-900 text-gray-500">
                <tr>
                  <th className="text-left font-normal py-1">Date</th>
                  <th className="text-left font-normal py-1">Status</th>
                  <th className="text-right font-normal py-1">Debits</th>
                  <th className="text-right font-normal py-1">Credits</th>
                </tr>
              </thead>
              <tbody>
                {data.days.map((d) => (
                  <tr key={d.businessDate} className={d.included ? '' : 'opacity-60'}>
                    <td className="py-0.5 font-mono text-gray-300">{d.businessDate}</td>
                    <td className={`py-0.5 ${STATUS_TONE[d.status] || 'text-gray-400'}`}>
                      {STATUS_LABEL[d.status] || d.status}
                      {d.status === 'already_exported' && d.previouslyExportedAt ? (
                        <span className="text-gray-600">
                          {' '}
                          {new Date(d.previouslyExportedAt).toLocaleDateString()}
                        </span>
                      ) : null}
                      {d.included && d.status === 'already_exported' ? (
                        <span className="text-amber-400"> · re-exporting</span>
                      ) : null}
                    </td>
                    <td className="py-0.5 text-right font-mono text-gray-400">
                      {d.included ? fmtCents(d.totalDebitCents) : ''}
                    </td>
                    <td className="py-0.5 text-right font-mono text-gray-400">
                      {d.included ? fmtCents(d.totalCreditCents) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.skipped.length > 0 && (
            <div className="mt-3 rounded border border-amber-500/30 bg-amber-500/5 p-2 text-xs">
              <div className="flex items-center gap-1 text-amber-300 mb-1">
                <AlertTriangle size={13} /> {data.skipped.length} day
                {data.skipped.length === 1 ? '' : 's'} not included
              </div>
              {data.skipped.map((sk) => (
                <div key={sk.businessDate} className="text-amber-200/70">
                  <span className="font-mono">{sk.businessDate}</span> — {sk.reason}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-lg bg-gray-900/50 border border-gray-700 p-3">
      <div className="text-[10px] uppercase tracking-wide text-gray-400">{label}</div>
      <div
        className={`text-base font-mono ${
          tone === 'good' ? 'text-emerald-400' : tone === 'bad' ? 'text-red-400' : 'text-white'
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function StatusPill({ status, overShort }) {
  if (status === 'closed') {
    const bad = Number(overShort) !== 0;
    return (
      <span
        className={`px-2 py-0.5 rounded text-xs ${
          bad ? 'bg-red-500/10 text-red-400' : 'bg-emerald-500/10 text-emerald-400'
        }`}
      >
        closed
      </span>
    );
  }
  return <span className="px-2 py-0.5 rounded text-xs bg-amber-500/10 text-amber-400">open</span>;
}

